import { InfoIcon } from "@phosphor-icons/react";
import { useEffect, useId, useRef, useState } from "react";
import "./infoHint.css";

/**
 * The ⓘ beside a statistics figure, build round 37 (2026-10-11). One glossary text behind it
 * (app/lib/statisticsGlossary.json), and no running text under the figures.
 *
 * Not the portal's `Hint`: that is a `details` element, which opens by keyboard and by tap but cannot
 * open on hover, because a closed `details` does not render its content for CSS to show. This is a
 * button. A mouse opens it by pointing and closes it by leaving; a tap or Enter opens and closes it;
 * Escape and a press anywhere else close it. The text is the button's description whether it is open
 * or not, so a screen reader reads it on focus without opening anything.
 */
export function InfoHint({ label, text, testId }: { label: string; text: string; testId?: string }) {
  const [open, setOpen] = useState(false);
  const [byPointer, setByPointer] = useState(false);
  const id = useId();
  const root = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const outside = (event: PointerEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <span
      className="info-hint"
      ref={root}
      onPointerEnter={(event) => {
        // Only a mouse hovers. A finger's pointerenter comes with its tap, which toggles below.
        if (event.pointerType === "mouse") {
          setByPointer(true);
          setOpen(true);
        }
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === "mouse" && byPointer) {
          setByPointer(false);
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        className="info-hint-button"
        aria-label={`Om ${label.toLowerCase()}`}
        aria-expanded={open}
        aria-describedby={id}
        data-testid={testId}
        onClick={() => {
          // A click on a text the mouse already opened keeps it open (it stays when the mouse leaves);
          // otherwise a click, a tap or Enter toggles it.
          if (byPointer) {
            setByPointer(false);
            setOpen(true);
            return;
          }
          setOpen((value) => !value);
        }}
      >
        <InfoIcon size={16} aria-hidden="true" />
      </button>
      <span id={id} role="tooltip" className="info-hint-text" hidden={!open} data-testid={testId ? `${testId}-text` : undefined}>
        {text}
      </span>
    </span>
  );
}
